import { Box } from '@material-ui/core'
import queryString from 'query-string'
import { useMemo, useState } from 'react'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import styled from 'styled-components'

import { isConsole } from '../../config/config'
import { useOnboardingChecklist } from '../../hooks/query/useOnboardingChecklist'
import { OnboardingChecklist, OnboardingChecklistName } from '../../models/onboarding'
import { SlimTeam } from '../../models/team'
import { User } from '../../models/user'
import { FlexBox } from '../Base/FlexBox'
import { Task, TaskList, TaskStatusEnum } from '../TaskList/TaskList'

interface OnboardingTaskListProps {
  user: User
  team: SlimTeam
  isCurrentUser?: boolean
  parseTaskList: (
    checklist: readonly OnboardingChecklist[],
    user: User,
    team: SlimTeam
  ) => ReadonlyArray<Task>
  children?: (
    tasks: ReadonlyArray<Task>,
    applicationTasks: ReadonlyArray<Task>,
    roadPreparationTasks: ReadonlyArray<Task>
  ) => React.ReactNode
}

const contractorPaymentsItems = [
  OnboardingChecklistName.CONTRACTOR_PAYMENTS_PROFILE,
  OnboardingChecklistName.PAYROLL_BANK_ACCOUNT,
  OnboardingChecklistName.ADD_CONTRACTOR,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_PROFILE,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_DIRECT_DEPOSIT,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_DEBIT_CARD,
]

export function OnboardingTaskList({
  user,
  team,
  isCurrentUser,
  parseTaskList,
  children,
}: OnboardingTaskListProps) {
  const userId = user.id
  const isCreditOnly = team.isCreditOnly
  const forceShowOnboardingTasks = Boolean(queryString.parse(window.location.search).onboarding)
  const [showAllItems, setShowAllItems] = useState(Boolean(queryString.parse(window.location.search).all))

  const { checklist, roadChecklist, isLoading } = useOnboardingChecklist(userId, {
    refetchOnMount: true,
  })

  const roadPreparationTasks = useMemo(() => {
    if (user && roadChecklist) {
      return parseTaskList(roadChecklist, user, team)
    }
    return []
  }, [roadChecklist, user, team, parseTaskList])

  const applicationTasksAll = useMemo(() => {
    if (user && checklist) {
      return parseTaskList(checklist, user, team)
    }
    return []
  }, [checklist, user, team, parseTaskList])

  if (isLoading) {
    return <InlineSpinner />
  }

  const applicationTasks = applicationTasksAll.filter(
    (t) => !contractorPaymentsItems.includes(t.name) || showAllItems
  )

  const showRoadChecklist =
    roadChecklist?.length &&
    (applicationTasks.every((t) =>
      [TaskStatusEnum.IN_PROGRESS, TaskStatusEnum.COMPLETED].includes(t.status)
    ) ||
      forceShowOnboardingTasks)

  function getTasks() {
    if (isConsole()) return applicationTasks
    if (showRoadChecklist) return roadPreparationTasks
    return applicationTasks
  }

  const tasks = getTasks()

  function renderHeader() {
    if (isConsole()) {
      return (
        <FlexBox justifyContent="space-between" alignItems="center" mb={-2}>
          <Text variant="header3" align="left">
            Application tasks
          </Text>
        </FlexBox>
      )
    }

    if (isCreditOnly) {
      return (
        <Text variant="header1" as="h1" styles="margin-top: 0;">
          {isCurrentUser ? 'Your' : `${user.firstName || 'Their'}'s`}{' '}
          {isCreditOnly ? 'credit application is in progress' : 'onboarding is in progress'}.
        </Text>
      )
    }

    if (showRoadChecklist) {
      if (!user?.isTeamAdmin) {
        return (
          <Text variant="header1" as="h1" styles="margin-top: 0;">
            Complete {isCurrentUser ? 'your' : ''} onboarding tasks
          </Text>
        )
      }

      return (
        <Text variant="header1" as="h1" styles="margin-top: 0;">
          Finish setting up your fleet
        </Text>
      )
    }

    return (
      <Text variant="header1" as="h1" styles="margin-top: 0;">
        Apply to drive with CloudTrucks
      </Text>
    )
  }

  function renderRoadPreparationInConsole() {
    if (!isConsole() || !roadPreparationTasks?.length) return null
    return (
      <>
        <Text variant="header3" mt={4}>
          Onboarding tasks
        </Text>
        <TaskList tasks={roadPreparationTasks} isReadOnly={false} />
      </>
    )
  }

  return (
    <>
      <Center>
        {renderHeader()}
        <Box m={isConsole() ? 4 : 5} />
      </Center>

      <TaskList tasks={tasks} isReadOnly={!isCurrentUser && !isConsole()} />
      {renderRoadPreparationInConsole()}

      {children?.(tasks, applicationTasks, roadPreparationTasks)}
      {isConsole() && applicationTasks?.length !== applicationTasksAll?.length ? (
        <Text variant="paragraph3" onClick={() => setShowAllItems(!showAllItems)} align="left" mt={2}>
          {showAllItems ? 'Hide' : 'Show'} all items
        </Text>
      ) : null}
    </>
  )
}

const Center = styled.div`
  text-align: center;
`
